import React, { useEffect, useState } from "react";
import { X, ExternalLink, Megaphone } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * One-shot popup for unread announcements.
 * Mounted at app shell; triggered when a user session is active.
 * Guarantees: at most ONE popup per app opening; after dismissal the
 * announcement is marked read on the server AND cached locally so the
 * same user never sees it again.
 */
export default function AnnouncementPopup() {
  const { user } = useAuth();
  const [ann, setAnn] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user?.id || checked) return;
    setChecked(true);
    (async () => {
      try {
        const res = await api.get("/api/announcements/next-popup");
        if (res.data?.announcement) {
          // localStorage justification: non-sensitive per-browser dedupe
          // flag ("has this announcement already been shown here").
          // No tokens or PII — just the announcement id.
          const key = `ann_seen:${user.id}:${res.data.announcement.id}`;
          if (!localStorage.getItem(key)) setAnn(res.data.announcement);
        }
      } catch (err) {
        // Non-fatal: popup is best-effort, never blocks the page.
        console.warn("announcement fetch failed (ignored):", err);
      }
    })();
  }, [user?.id, checked]);

  const dismiss = async () => {
    if (!ann) return;
    try {
      await api.post(`/api/announcements/${ann.id}/read`);
      localStorage.setItem(`ann_seen:${user?.id}:${ann.id}`, "1");
    } catch (err) {
      // Non-fatal: dismiss is server-authoritative via /read; the local
      // flag is only a flicker-prevention optimisation.
      console.warn("announcement dismiss failed (ignored):", err);
    }
    setAnn(null);
  };

  if (!ann) return null;
  return (
    <div
      data-testid="announcement-popup-overlay"
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={dismiss}
    >
      <div
        data-testid="announcement-popup"
        className="bg-[#14161C] border border-[#FF6B00]/40 rounded-2xl w-full max-w-md p-5 relative shadow-2xl space-y-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="announcement-popup-close"
          onClick={dismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-[10px] font-bold text-[#FF6B00] uppercase tracking-wider">
          <Megaphone className="w-3.5 h-3.5" />
          <span>Comunicado</span>
        </div>
        {ann.image_url && (
          <img src={ann.image_url} alt="" className="w-full h-40 object-cover rounded-lg" />
        )}
        <div className="text-white font-extrabold text-base">{ann.title}</div>
        <div className="text-sm text-gray-300 whitespace-pre-wrap">{ann.message}</div>
        <div className="flex items-center justify-between pt-2">
          {ann.cta_url ? (
            <a
              data-testid="announcement-popup-cta"
              href={ann.cta_url}
              target="_blank"
              rel="noreferrer"
              onClick={dismiss}
              className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase bg-[#FF6B00] text-black px-3 py-1.5 rounded-lg"
            >
              <ExternalLink className="w-3.5 h-3.5" /> {ann.cta_label || "Saiba mais"}
            </a>
          ) : <span />}
          <button
            data-testid="announcement-popup-dismiss"
            onClick={dismiss}
            className="text-[11px] font-extrabold uppercase bg-[#1F2330] border border-[#3E4760] text-white px-3 py-1.5 rounded-lg"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}
